import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ExternalLink, Loader2, SkipForward, Upload } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { EvidenceCoverageSummary } from '../case/EvidenceCoverageSummary';

const SchemaBlock = ({ needs }) => (
  <div className="space-y-2 text-xs" data-testid="cb-schema">
    <div><span className="font-semibold">File type:</span> {needs.file_types.join(' or ')}</div>
    <div>
      <div className="font-semibold mb-1">Required columns</div>
      <div className="flex flex-wrap gap-1" data-testid="cb-required-columns">
        {needs.required.map((c) => <code key={c} className="rounded bg-slate-900 px-1.5 py-0.5 text-[11px] text-white">{c}</code>)}
      </div>
    </div>
    <div>
      <div className="font-semibold mb-1">{needs.optional_label} columns</div>
      <div className="flex flex-wrap gap-1" data-testid="cb-optional-columns">
        {needs.optional.map((c) => <code key={c} className="rounded border bg-white px-1.5 py-0.5 text-[11px]">{c}</code>)}
      </div>
    </div>
    <div>
      <div className="font-semibold mb-1">Example row</div>
      <div className="overflow-x-auto rounded border bg-white" data-testid="cb-example-row">
        <table className="ds-compact text-[11px]">
          <thead><tr>{Object.keys(needs.example_row).map((k) => <th key={k} className="px-2 py-1 text-left font-mono font-medium border-b">{k}</th>)}</tr></thead>
          <tbody><tr>{Object.values(needs.example_row).map((v, i) => <td key={i} className="px-2 py-1 font-mono whitespace-nowrap">{v}</td>)}</tr></tbody>
        </table>
      </div>
    </div>
  </div>
);

const ActionButton = ({ a, busy, onAction, onPickFile }) => {
  if (a.href) {
    return <Link to={a.href} data-testid={`cb-action-${a.id}`}><Button size="sm" variant="outline" className="h-10 text-xs"><ExternalLink className="w-3.5 h-3.5 mr-1" />{a.label}</Button></Link>;
  }
  const primary = ['upload', 'start', 'run_matching', 'apply_matching', 'attach_now', 'run_completeness', 'continue'].includes(a.id);
  const Icon = a.id === 'upload' || a.id === 'attach_now' ? Upload : a.id === 'skip' ? SkipForward : CheckCircle2;
  return (
    <Button size="sm" variant={primary ? 'default' : 'outline'} className="h-10 text-xs" disabled={busy} data-testid={`cb-action-${a.id}`}
            onClick={() => (a.id === 'upload' || a.id === 'attach_now' ? onPickFile(a.id) : onAction(a))}>
      <Icon className="w-3.5 h-3.5 mr-1" />{a.label}
    </Button>
  );
};

const Matching = ({ m }) => (
  <div className="text-xs space-y-1" data-testid="cb-matching">
    <div className="flex flex-wrap gap-1">
      <Badge variant="outline" data-testid="cb-band-auto">Auto {m.bands.auto}</Badge>
      <Badge variant="outline" data-testid="cb-band-review">Review {m.bands.review}</Badge>
      <Badge variant="outline" data-testid="cb-band-block">Blocked {m.bands.block}</Badge>
      <Badge variant="outline" data-testid="cb-matching-status">{m.status === 'applied' ? `Applied${m.autonomous ? ' (autonomous)' : ''}`
        : m.status === 'partially_applied' ? `Partially applied (${m.applied} auto). Review band awaits your confirmation` : 'Awaiting your confirmation'}</Badge>
    </div>
    <ul className="space-y-0.5">
      {m.proposals.slice(0, 6).map((p) => <li key={p.case_item_id}><span className="font-mono">{p.sku}</span> → {p.import_mrn} · {Math.round(p.confidence * 100)}% · {p.band}{p.ambiguous ? ' · ambiguous' : ''}</li>)}
      {m.blocked.slice(0, 6).map((b) => <li key={b.case_item_id} className="text-rose-700"><span className="font-mono">{b.sku}</span> blocked: {b.detail}</li>)}
    </ul>
  </div>
);

const Readiness = ({ r, onOverride }) => (
  <div className="space-y-2 text-xs" data-testid="cb-readiness">
    <Badge variant="outline" className={r.ready ? 'text-emerald-700 border-emerald-300' : 'text-rose-700 border-rose-300'} data-testid="cb-readiness-label">{r.label}</Badge>
    {r.override && <div className="text-amber-800 font-semibold" data-testid="cb-override-banner">Evidence override applied. Audit risk increased.</div>}
    {r.next_actions.length > 0 && <ul className="list-disc pl-4">{r.next_actions.map((n) => <li key={n}>{n}</li>)}</ul>}
    {r.suggestion && <div className="text-amber-800" data-testid="cb-override-suggestion">{r.suggestion}</div>}
    {r.override_allowed && <Button size="sm" className="h-10 text-xs bg-amber-600 hover:bg-amber-700" onClick={onOverride} data-testid="cb-override-btn">Override evidence (admin)</Button>}
  </div>
);

export const CaseBuilderStepCard = ({ card, busy, onAction, onFile, onOverride }) => {
  const fileRef = useRef(null);
  const [pick, setPick] = useState(null);
  const [itemId, setItemId] = useState('none');
  const pickFile = (kind) => { setPick(kind); fileRef.current?.click(); };
  const ex = card.exceptions;
  return (
    <div className="rounded-xl border border-slate-300 bg-white p-3 space-y-3 shadow-sm" data-testid="cb-step-card">
      <div className="flex items-center gap-2">
        <Badge className="bg-slate-900 text-[10px]" data-testid="cb-step-number">Step {card.step}</Badge>
        {card.case_reference && <span className="text-[11px] text-slate-500" data-testid="cb-case-ref">{card.case_reference}</span>}
      </div>
      <h3 className="text-sm font-semibold leading-tight" data-testid="cb-step-title">{card.title}</h3>
      {card.needs && (<><div className="text-xs font-semibold">What Morgan needs</div><SchemaBlock needs={card.needs} /></>)}
      <div className="text-xs"><span className="font-semibold">Why: </span><span data-testid="cb-why">{card.why}</span></div>
      <div className="text-xs"><span className="font-semibold">What happens next: </span><span data-testid="cb-next">{card.next}</span></div>
      {[...(card.warnings || []), ...(card.upload_result?.warnings || [])].map((w) => (
        <div key={w} className="flex gap-1 rounded bg-amber-50 px-2 py-1 text-xs text-amber-900" data-testid="cb-warning"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{w}</div>
      ))}
      {card.upload_error && <div className="rounded bg-rose-50 px-2 py-1 text-xs text-rose-800" data-testid="cb-upload-error">{card.upload_error.message}</div>}
      {card.upload_result && <div className="text-xs text-emerald-700" data-testid="cb-upload-result">{card.upload_result.filename}: {card.upload_result.rows} rows accepted.</div>}
      {card.blocked && <ul className="text-xs text-rose-700 list-disc pl-4" data-testid="cb-blocked">{card.blocked_reasons.map((b) => <li key={b}>{b}</li>)}</ul>}
      {card.matching && <Matching m={card.matching} />}
      {ex && (ex.current ? (
        <div className="text-xs space-y-1" data-testid="cb-exception">
          <div className="font-semibold">{ex.current.label} ({ex.current.count}) · category {ex.index + 1} of {ex.categories.length}</div>
          <ul className="space-y-0.5">{ex.current.rows.map((r, i) => <li key={r.case_item_id || i}><span className="font-mono">{r.sku}</span> {r.detail}</li>)}</ul>
        </div>
      ) : <div className="text-xs text-emerald-700" data-testid="cb-no-exceptions">{ex.message}</div>)}
      {card.line_items && card.attach_permitted && (
        <Select value={itemId} onValueChange={setItemId}>
          <SelectTrigger className="h-10 text-xs" data-testid="cb-evidence-item"><SelectValue /></SelectTrigger>
          <SelectContent className="z-[60]">
            <SelectItem value="none">Whole case (multi-line document)</SelectItem>
            {card.line_items.map((i) => <SelectItem key={i.id} value={i.id}>{i.sku} · qty {i.quantity}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {card.hint && <div className="text-[11px] text-slate-500">{card.hint}</div>}
      {card.documents?.length > 0 && (
        <ul className="space-y-0.5 text-xs" data-testid="cb-attached-docs">
          {card.documents.map((doc) => <li key={doc.id} data-testid={`cb-doc-${doc.id}`}>{doc.filename} · {doc.source === 'morgan' ? 'via Morgan' : 'case page'} · extraction {doc.extraction || 'pending'}{doc.method ? ` (${doc.method.replace(/_/g, ' ')})` : ''}</li>)}
        </ul>
      )}
      {card.coverage !== undefined && <EvidenceCoverageSummary coverage={card.coverage} compact testPrefix="cb-coverage" />}
      {card.readiness && <Readiness r={card.readiness} onOverride={onOverride} />}
      <input ref={fileRef} type="file" className="hidden" data-testid="cb-file-input"
             accept={pick === 'attach_now' ? '.pdf,.png,.jpg,.jpeg,.webp' : '.csv,.xlsx'}
             onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onFile(f, pick, itemId === 'none' ? null : itemId); }} />
      <div className="flex flex-wrap gap-2" data-testid="cb-actions">
        {busy && <Loader2 className="w-4 h-4 animate-spin self-center" />}
        {(card.actions || []).map((a) => <ActionButton key={`${a.id}-${a.step || ''}`} a={a} busy={busy} onAction={onAction} onPickFile={pickFile} />)}
      </div>
    </div>
  );
};
